
import { useSnackbar } from '@components/feedback/GlobalSnackbar';
import { useApiError } from '@hooks/useApiError';
import { CameraAltOutlined, CheckCircleOutlined, EmailOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { Avatar, Box, CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { useAuthStore } from '@store/authStore';
import { useRef, useState } from 'react';

import { CARD, FIELD_DISABLED, LABEL, TITLE } from '../styles';
import { useResendVerification, useUploadAvatar } from '../useSettingsData';

import { EmailVerificationDialog } from './EmailVerificationDialog';

export const ProfileCard = () => {
  const user    = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const initials = user?.name
    ? user.name.split(' ').filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';
  const { show: showSnack } = useSnackbar();
  const uploadAvatar = useUploadAvatar();
  const resendVerification = useResendVerification();

  const avatarInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;

    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      showSnack('Only JPG, JPEG or PNG images are allowed', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showSnack('Image must be under 5 MB', 'error');
      return;
    }

    try {
      const { data } = await uploadAvatar.mutateAsync(file);
      setUser(data.user);
      showSnack('Profile photo updated', 'success');
    } catch (err) {
      const e2 = err as { response?: { data?: { error?: { message?: string } } } };
      showSnack(e2.response?.data?.error?.message ?? 'Could not upload photo', 'error');
    }
  };

  const [verifyEmailOpen,       setVerifyEmailOpen]       = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);

  const handleNotVerifiedClick = async () => {
    if (isSendingVerification) return;
    setIsSendingVerification(true);
    try {
      await resendVerification.mutateAsync();
      setVerifyEmailOpen(true);
    } catch (err) {
      const errorMsg = useApiError(err) ?? 'Failed to send verification code.';
      showSnack(errorMsg, 'error');
    } finally {
      setIsSendingVerification(false);
    }
  };

  const handleEmailVerified = () => {
    if (user) setUser({ ...user, emailVerified: true });
    setVerifyEmailOpen(false);
  };

  return (
    <>
    <Box sx={CARD}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '20px' }}>
        <Typography sx={TITLE}>Profile</Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: '16px', alignItems: 'center' }}>
        {/* Left: avatar + name + badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar src={user?.avatarUrl ?? undefined} sx={(theme) => ({ width: 72, height: 72, bgcolor: theme.palette.primary.main, fontSize: 26, fontWeight: 700 })}>
              {initials}
            </Avatar>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              hidden
              onChange={handleAvatarPick}
            />
            <IconButton
              size="small"
              disabled={uploadAvatar.isPending}
              onClick={() => avatarInputRef.current?.click()}
              sx={(theme) => ({
                position: 'absolute', bottom: 0, right: 0,
                width: 26, height: 26, borderRadius: '50%',
                bgcolor: theme.palette.grey[900], color: 'white',
                border: '2px solid white',
                '&:hover': { bgcolor: theme.palette.grey[700] },
              })}
            >
              {uploadAvatar.isPending ? <CircularProgress size={12} sx={{ color: 'white' }} /> : <CameraAltOutlined sx={{ fontSize: 12 }} />}
            </IconButton>
          </Box>
          <Box>
            <Typography sx={(theme) => ({ fontSize: 18, fontWeight: 700, color: theme.palette.grey[900], mb: '6px' })}>
              {user?.name ?? '—'}
            </Typography>
            <Box sx={(theme) => ({
              display: 'inline-flex', alignItems: 'center',
              bgcolor: theme.palette.status.info.bg, borderRadius: '20px', px: '12px', py: '3px',
            })}>
              <Typography sx={(theme) => ({ fontSize: 12, fontWeight: 600, color: theme.palette.primary.main, textTransform: 'capitalize' })}>
                {user?.role ?? '—'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right: email field */}
        <Box>
          <Typography component="label" sx={LABEL}>Email Address</Typography>
          <TextField
            value={user?.email ?? ''}
            disabled
            fullWidth
            sx={FIELD_DISABLED}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={(theme) => ({ fontSize: 18, color: theme.palette.grey[400] })} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {user?.emailVerified ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <CheckCircleOutlined sx={{ fontSize: 15, color: '#16a34a' }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>Verified</Typography>
                      </Box>
                    ) : (
                      <Box
                        onClick={handleNotVerifiedClick}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: '5px',
                          cursor: isSendingVerification ? 'default' : 'pointer',
                          opacity: isSendingVerification ? 0.7 : 1,
                          '&:hover': { opacity: isSendingVerification ? 0.7 : 0.75 },
                        }}
                      >
                        {isSendingVerification
                          ? <CircularProgress size={13} sx={{ color: '#f59e0b' }} />
                          : <WarningAmberOutlined sx={{ fontSize: 15, color: '#f59e0b' }} />}
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#f59e0b' }}>
                          {isSendingVerification ? 'Sending…' : 'Not Verified'}
                        </Typography>
                      </Box>
                    )}
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      </Box>
    </Box>

    <EmailVerificationDialog
      open={verifyEmailOpen}
      email={user?.email ?? ''}
      onClose={() => setVerifyEmailOpen(false)}
      onVerified={handleEmailVerified}
    />
    </>
  );
};
